import type { FC, JSX, MouseEvent } from 'react';

import { memo, useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { media } from '../../core/styles';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const style = {
  CloseButton: css`
    ${({ theme }) => css`
      position: absolute;
      top: 50%;
      content: ' ';
      right: ${theme.size[4]};
      margin-top: -1px;
      width: ${theme.size.component['close-button']};
      height: ${theme.borders.width.sm};
      background-color: ${theme.colors.slate['600']};

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          height: ${theme.borders.width.xs};
          right: ${theme.size[5]};
          margin-top: 0;
        `,
      })}
    `}
  `,
};

const S = {
  Title: styled.button`
    ${({ theme }) => css`
      border: 0;
      cursor: pointer;
      display: block;
      font-family: ${theme.font.family.primary};
      font-size: ${theme.font.size[3]};
      position: relative;
      text-align: start;
      width: 100%;

      &[data-root='true'] {
        color: ${theme.colors.content.primary};
        font-weight: ${theme.variables.font.weight.medium};
        height: ${theme.size[16]};
        line-height: 16px;
        padding: ${theme.padding.xs} ${theme.padding['2xs']} ${theme.padding.xs} ${theme.padding.sm};
        text-transform: uppercase;
      }

      &[data-root='false'] {
        color: ${theme.colors.content.secondary};
        font-weight: ${theme.variables.font.weight.regular};
        height: ${theme.size[12]};
        line-height: 1.4;
        padding: ${theme.padding['2xs']} ${theme.padding['2xs']} ${theme.padding['2xs']}
          ${theme.padding.sm};
        text-transform: capitalize;
      }

      &[data-expanded='true']::before {
        transform: rotate(0);
      }

      &[data-expanded='false']::before {
        transform: rotate(90deg);
      }

      &::before {
        ${style.CloseButton}
        transition: transform 0.25s ease-in-out;

        &[data-root='true'] {
          transition: transform 0.5s ease-in-out;

          @media (prefers-reduced-motion) {
            transition: none;
          }
        }

        @media (prefers-reduced-motion) {
          transition: none;
        }
      }

      &::after {
        ${style.CloseButton}
      }

      @media ${media(theme).greaterThan('sm')} {
        &[data-root='true'] {
          height: ${theme.size[16]};
        }

        &[data-root='false'] {
          height: ${theme.size[8]};
          padding: ${theme.padding['3xs']} ${theme.padding['2xs']} ${theme.padding['3xs']}
            ${theme.padding.sm};
        }
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: css`
          &[data-expanded='true'][data-root='false'] {
            color: ${theme.colors.content.warning};
          }

          /* stylelint-disable-next-line a11y/selector-pseudo-class-focus */
          &[data-root='false']:hover {
            color: ${theme.colors.content.warning};
          }

          &::before,
          &::after {
            background-color: ${theme.colors.content.component.light};
          }
        `,
        [SiteType.Aaf]: css`
          display: inline-block;
          padding: ${`${theme.padding['2xs']} ${theme.padding.xs} ${theme.padding['2xs']} ${theme.padding.sm}`};

          &[data-root='true'] {
            height: unset;
            font-weight: ${theme.variables.font.weight.bold};
          }

          &[data-root='false'] {
            height: unset;
            font-weight: ${theme.variables.font.weight.regular};
          }

          &::before,
          &::after {
            background-color: ${theme.colors.content.component['close-button']};
          }

          @media ${media(theme).greaterThan('sm')} {
            height: unset;

            &[data-root='true'] {
              padding: ${`${theme.padding['2xs']} ${theme.padding.xs} ${theme.padding['2xs']} ${theme.padding.sm}`};
            }
          }
        `,
      })}
    `}
  `,

  TitleText: styled.div`
    padding-right: 32px;
  `,
};

interface Props {
  activeIndexArray?: string[];
  currentIndex: string;
  updateActiveItem: (arr: string[]) => void;
  isExpanded: boolean;
  depthLevel: number;
  classNames?: string;
  dataNavItem?: string;
  parentActive: boolean;
  uid?: string | null;
  styleObject?: { [key: string]: string };
  label?: string | null;
}

const SideNavItemTitleComponent: FC<Props & JSX.IntrinsicElements['button']> = ({
  updateActiveItem,
  isExpanded,
  activeIndexArray,
  currentIndex,
  depthLevel,
  classNames,
  dataNavItem,
  parentActive,
  uid,
  styleObject,
  label,
  onClick,
  ...rest
}) => {
  const isRoot = depthLevel === 0;
  const handleTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (activeIndexArray) {
        const currentIndexToArr = currentIndex.split(',');

        if (`${activeIndexArray.slice(0, currentIndexToArr.length)}` === `${currentIndexToArr}`) {
          const updatedArray = [...activeIndexArray];

          updatedArray[currentIndexToArr.length - 1] = '-1';
          updateActiveItem(updatedArray);
        } else {
          updateActiveItem([...currentIndexToArr]);
        }
      }
    },
    [activeIndexArray, currentIndex, onClick, updateActiveItem]
  );

  const innerHtml = useMemo(
    () => ({
      __html: label ?? '',
    }),
    [label]
  );

  return (
    <S.Title
      onClick={handleTitleClick}
      data-root={isRoot}
      data-expanded={isExpanded}
      data-nav-item={dataNavItem}
      aria-expanded={isExpanded}
      className={classNames}
      aria-disabled={!parentActive}
      tabIndex={parentActive ? undefined : -1}
      data-testid={uid === 'myAccountNode' ? 'sidenav-myaccount' : undefined}
      {...rest}
    >
      <S.TitleText style={styleObject} dangerouslySetInnerHTML={innerHtml} />
    </S.Title>
  );
};

export const SideNavItemTitle = memo(SideNavItemTitleComponent, isEqual);
